import React, { useMemo, useState } from 'react';
import { ClipboardList, CopyCheck, Repeat } from 'lucide-react';

export type BulkMode = 'none' | 'recur' | 'multi' | 'paste';

type Props = {
  mode: BulkMode;
  onModeChange: (mode: BulkMode) => void;
  existingDates: Set<string>;
  multiSelectedDates: string[];
  onClearMulti: () => void;
  onAddDates: (isoDates: string[]) => void;
  compact?: boolean;
};

const pad = (n: number) => String(n).padStart(2, '0');
const toIso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const parseIso = (value: string): Date | null => {
  const parts = value.split('-').map(Number);
  if (parts.length < 3 || !parts[0] || !parts[1] || !parts[2]) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
};

/** Formats acceptés : 2026-07-02, 02/07/2026, 02.07.2026, 02/07/26. */
export const parsePastedDates = (text: string): string[] => {
  const lines = text
    .split(/[\n,;]+/)
    .map((line) => line.trim())
    .filter(Boolean);

  const out: string[] = [];
  for (const line of lines) {
    let iso: string | null = null;
    let match = line.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (match) {
      iso = `${match[1]}-${pad(Number(match[2]))}-${pad(Number(match[3]))}`;
    } else {
      match = line.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{2,4})$/);
      if (match) {
        const year = match[3].length === 2 ? `20${match[3]}` : match[3];
        iso = `${year}-${pad(Number(match[2]))}-${pad(Number(match[1]))}`;
      }
    }
    if (iso && !out.includes(iso)) out.push(iso);
  }
  return out;
};

type Frequency = 'weekly' | 'biweekly' | 'monthly';

const FREQUENCIES: Array<{ id: Frequency; label: string }> = [
  { id: 'weekly', label: 'Chaque semaine' },
  { id: 'biweekly', label: 'Toutes les 2 sem.' },
  { id: 'monthly', label: 'Chaque mois' },
];

const DAY_LABELS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const computeRecurrenceDates = (
  frequency: Frequency,
  weekdays: number[],
  startIso: string,
  endIso: string
): string[] => {
  const start = parseIso(startIso);
  const end = parseIso(endIso);
  if (!start || !end || end < start) return [];

  const out: string[] = [];

  if (frequency === 'monthly') {
    const cursor = new Date(start);
    while (cursor <= end) {
      out.push(toIso(cursor));
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return out;
  }

  const firstMonday = new Date(start);
  firstMonday.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  const cursor = new Date(start);
  while (cursor <= end) {
    const dayIndex = (cursor.getDay() + 6) % 7;
    if (weekdays.includes(dayIndex)) {
      if (frequency === 'biweekly') {
        const week = Math.floor((cursor.getTime() - firstMonday.getTime()) / (7 * 86400000));
        if (week % 2 === 0) out.push(toIso(cursor));
      } else {
        out.push(toIso(cursor));
      }
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
};

const toolButtonClass = (active: boolean) =>
  [
    'inline-flex items-center gap-1.5 rounded-xl border-2 px-3 py-2 text-xs font-bold transition-colors',
    active
      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
      : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-200 hover:bg-indigo-50/40',
  ].join(' ');

export const ProgramBulkTools: React.FC<Props> = ({
  mode,
  onModeChange,
  existingDates,
  multiSelectedDates,
  onClearMulti,
  onAddDates,
  compact = false,
}) => {
  const today = toIso(new Date());
  const [frequency, setFrequency] = useState<Frequency>('weekly');
  const [weekdays, setWeekdays] = useState<number[]>([1]);
  const [recurStart, setRecurStart] = useState(today);
  const [recurEnd, setRecurEnd] = useState('');
  const [pasteText, setPasteText] = useState('');

  const toggleMode = (next: BulkMode) => {
    onModeChange(mode === next ? 'none' : next);
  };

  const recurrenceDates = useMemo(
    () =>
      recurEnd
        ? computeRecurrenceDates(frequency, weekdays, recurStart, recurEnd).filter(
            (iso) => !existingDates.has(iso) && iso >= today
          )
        : [],
    [frequency, weekdays, recurStart, recurEnd, existingDates, today]
  );

  const pastedDates = useMemo(() => parsePastedDates(pasteText), [pasteText]);
  const pastedNew = pastedDates.filter((iso) => !existingDates.has(iso));
  const pastedPast = pastedNew.filter((iso) => iso < today).length;

  const toggleWeekday = (index: number) => {
    setWeekdays((current) =>
      current.includes(index) ? current.filter((d) => d !== index) : [...current, index].sort()
    );
  };

  return (
    <div className={compact ? 'mb-3' : 'mb-4'}>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => toggleMode('recur')} className={toolButtonClass(mode === 'recur')}>
          <Repeat className="h-3.5 w-3.5" />
          Récurrence
        </button>
        <button type="button" onClick={() => toggleMode('multi')} className={toolButtonClass(mode === 'multi')}>
          <CopyCheck className="h-3.5 w-3.5" />
          Sélection multiple
        </button>
        <button type="button" onClick={() => toggleMode('paste')} className={toolButtonClass(mode === 'paste')}>
          <ClipboardList className="h-3.5 w-3.5" />
          Coller des dates
        </button>
      </div>

      {mode === 'recur' && (
        <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <p className="mb-3 text-sm font-bold text-slate-800">Répéter automatiquement</p>

          <div className="mb-3 flex flex-wrap gap-2">
            {FREQUENCIES.map((freq) => (
              <button
                key={freq.id}
                type="button"
                onClick={() => setFrequency(freq.id)}
                className={[
                  'rounded-lg border-2 px-3 py-1.5 text-xs font-bold transition-colors',
                  frequency === freq.id
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-200',
                ].join(' ')}
              >
                {freq.label}
              </button>
            ))}
          </div>

          {frequency !== 'monthly' && (
            <div className="mb-3">
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Jours de la semaine
              </p>
              <div className="flex gap-1.5">
                {DAY_LABELS.map((label, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => toggleWeekday(index)}
                    className={[
                      'h-9 w-9 rounded-lg border-2 text-sm font-bold transition-colors',
                      weekdays.includes(index)
                        ? 'border-indigo-600 bg-indigo-600 text-white'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-300',
                    ].join(' ')}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mb-3 grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-400">Du</label>
              <input
                type="date"
                value={recurStart}
                min={today}
                onChange={(e) => setRecurStart(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:border-indigo-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-400">Au</label>
              <input
                type="date"
                value={recurEnd}
                min={recurStart}
                onChange={(e) => setRecurEnd(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:border-indigo-500 focus:outline-none"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-slate-600">
              <strong className="text-indigo-600">{recurrenceDates.length}</strong> nouvelle
              {recurrenceDates.length > 1 ? 's' : ''} séance{recurrenceDates.length > 1 ? 's' : ''}
            </span>
            <button
              type="button"
              disabled={recurrenceDates.length === 0}
              onClick={() => {
                onAddDates(recurrenceDates);
                onModeChange('none');
              }}
              className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Générer
            </button>
          </div>
        </div>
      )}

      {mode === 'multi' && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-indigo-200 bg-indigo-50/60 px-4 py-3">
          <span className="text-sm text-slate-700">
            Cliquez les jours dans le calendrier —{' '}
            <strong className="text-indigo-600">{multiSelectedDates.length}</strong> sélectionné
            {multiSelectedDates.length > 1 ? 's' : ''}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClearMulti}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Effacer
            </button>
            <button
              type="button"
              disabled={multiSelectedDates.length === 0}
              onClick={() => {
                onAddDates(multiSelectedDates.filter((iso) => !existingDates.has(iso)));
                onClearMulti();
                onModeChange('none');
              }}
              className="rounded-lg bg-indigo-600 px-3.5 py-1.5 text-xs font-bold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Ajouter {multiSelectedDates.length > 0 ? multiSelectedDates.length : ''}
            </button>
          </div>
        </div>
      )}

      {mode === 'paste' && (
        <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <p className="text-sm font-bold text-slate-800">Collez une liste de dates</p>
          <p className="mb-2.5 mt-0.5 text-xs text-slate-500">
            Une par ligne (ou séparées par des virgules) — formats acceptés : 2026-07-02, 02/07/2026, 02.07.2026
          </p>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={4}
            placeholder={'2026-07-02\n09/07/2026\n16/07/2026'}
            className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 font-mono text-sm text-slate-900 focus:border-indigo-500 focus:outline-none"
          />
          <div className="mt-2.5 flex items-center justify-between gap-3">
            <span className="text-xs text-slate-500">
              {pasteText.trim() ? (
                <>
                  <strong className="text-indigo-600">{pastedNew.length}</strong> date
                  {pastedNew.length > 1 ? 's' : ''} reconnue{pastedNew.length > 1 ? 's' : ''}
                  {pastedDates.length - pastedNew.length > 0 && (
                    <> · {pastedDates.length - pastedNew.length} déjà planifiée(s)</>
                  )}
                  {pastedPast > 0 && <> · {pastedPast} dans le passé</>}
                </>
              ) : (
                ' '
              )}
            </span>
            <button
              type="button"
              disabled={pastedNew.length === 0}
              onClick={() => {
                onAddDates(pastedNew);
                setPasteText('');
                onModeChange('none');
              }}
              className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Ajouter les dates
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
